import { IRecordingStorageProvider } from './IRecordingStorageProvider';
import { IndexedDBManager, IndexedDBConnectionError } from '../../utils/IndexedDBManager';
import { IRecording } from '../RecordingState';

/**
 * IndexedDB-based recording storage provider.
 * Wraps the existing IndexedDBManager with the IRecordingStorageProvider interface.
 */
export class IndexedDBRecordingProvider implements IRecordingStorageProvider<IRecording> {
    constructor(private indexedDB: IndexedDBManager<IRecording>) {}

    async add(data: IRecording): Promise<string> {
        const key = await this.indexedDB.add(data);
        return String(key);
    }

    async get(id: string): Promise<IRecording | undefined> {
        return this.indexedDB.get(id);
    }

    async getAll(): Promise<IRecording[]> {
        return this.indexedDB.getAll();
    }

    async update(data: IRecording): Promise<void> {
        return this.indexedDB.update(data);
    }

    async delete(id: string): Promise<void> {
        return this.indexedDB.delete(id);
    }

    /**
     * Tests if IndexedDB is available by attempting to get all records.
     * The underlying IndexedDBManager has retry logic that will throw
     * IndexedDBConnectionError after exhausting retries.
     */
    async isAvailable(): Promise<boolean> {
        try {
            await this.indexedDB.getAll();
            return true;
        } catch (error) {
            if (error instanceof IndexedDBConnectionError) {
                return false;
            }
            // Re-throw unexpected errors
            throw error;
        }
    }
}
